<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import SectionLayout from './components/SectionLayout.vue';
import ImageCropperModal from 'dashboard/components-next/ImageCropperModal.vue';
import moduleMixin from 'dashboard/mixins/moduleMixin';

const BRANDING_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BRANDING_IMAGE_SIZE = 5 * 1024 * 1024;

export default {
  components: {
    BaseSettingsHeader,
    NextButton,
    AccountId,
    BuildInfo,
    AccountDelete,
    SectionLayout,
    ImageCropperModal,
    WithLabel,
    NextInput,
  },
  mixins: [moduleMixin],
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      brandName: '',
      loginTitle: '',
      whiteLabelSenderInboxId: '',
      locale: 'en',
      inboundEmailDomain: '',
      inboundEmailContinuityConfigured: false,
      supportEmail: '',
      features: {},
      showCropper: false,
      cropperImageSrc: null,
      showFaviconCropper: false,
      cropperFaviconImageSrc: null,
      showLogoLoginCropper: false,
      cropperLogoLoginImageSrc: null,
      autoCreateTicketOnResolve: false,
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      inboxes: 'inboxes/getInboxes',
      uiFlags: 'accounts/getUIFlags',
      isOnLumeratecCloud: 'globalConfig/isOnLumeratecCloud',
    }),
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    whiteLabelEmailInboxes() {
      return (this.inboxes || []).filter(
        inbox =>
          inbox.channel_type === 'Channel::Email' &&
          inbox.can_send_outbound_email &&
          inbox.email
      );
    },
  },
  watch: {
    'currentAccount.id'(id) {
      if (id) {
        this.initializeAccount();
      }
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');

    // Account already in the store (navigated in): seed immediately.
    if (this.currentAccount.id) {
      this.initializeAccount();
    }
  },
  methods: {
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          inbound_email_domain,
          inbound_email_continuity_configured,
          support_email,
          features,
          white_label_sender_inbox_id,
        } = this.getAccount(this.accountId);

        const effectiveLocale = this.uiSettings?.locale || locale;
        if (effectiveLocale) {
          this.$root.$i18n.locale = effectiveLocale;
        }
        this.name = name;
        this.brandName = this.currentAccount.brand_name || '';
        this.loginTitle = this.currentAccount.login_title || '';
        this.whiteLabelSenderInboxId = white_label_sender_inbox_id
          ? String(white_label_sender_inbox_id)
          : '';
        this.locale = locale;
        this.id = id;
        this.inboundEmailDomain = inbound_email_domain || '';
        this.inboundEmailContinuityConfigured =
          inbound_email_continuity_configured || false;

        this.supportEmail = support_email;
        this.features = features;
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }

      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          support_email: this.supportEmail,
          custom_attributes: {
            ...this.currentAccount.custom_attributes,
            brand_name: this.brandName,
            login_title: this.loginTitle,
          },
        });
        // If user locale is set, update the locale with user locale
        const updatedLocale = this.uiSettings?.locale || this.locale;
        if (updatedLocale) {
          this.$root.$i18n.locale = updatedLocale;
        }
        this.getAccount(this.id).locale = this.locale;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    triggerLogoUpload() {
      this.$refs.logoInput.click();
    },

    validateBrandingFile(file) {
      if (!BRANDING_IMAGE_TYPES.includes(file.type)) {
        useAlert(
          this.$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.INVALID_IMAGE_TYPE')
        );
        return false;
      }

      if (file.size > MAX_BRANDING_IMAGE_SIZE) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.IMAGE_TOO_LARGE'));
        return false;
      }

      return true;
    },

    releasePreviewUrl(property) {
      const url = this[property];
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
      this[property] = null;
    },

    onLogoFileSelected(event) {
      const file = event.target.files[0];
      if (file && this.validateBrandingFile(file)) {
        this.cropperImageSrc = URL.createObjectURL(file);
        this.showCropper = true;
      }
      event.target.value = ''; // reset
    },

    closeCropper() {
      this.showCropper = false;
      this.releasePreviewUrl('cropperImageSrc');
    },

    async handleSaveLogo(blob) {
      const formData = new FormData();
      formData.append('custom_logo', blob, 'logo.png');
      try {
        await this.$store.dispatch('accounts/update', formData);
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.LOGO_UPDATED'));
        this.closeCropper();
      } catch (error) {
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.LOGO_UPDATE_ERROR'));
      }
    },

    async saveBrandName() {
      try {
        await this.$store.dispatch('accounts/update', {
          custom_attributes: {
            ...this.currentAccount.custom_attributes,
            brand_name: this.brandName,
          },
        });
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.BRAND_NAME_SAVED'));
      } catch (error) {
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.BRAND_NAME_SAVE_ERROR'));
      }
    },

    async saveLoginTitle() {
      try {
        await this.$store.dispatch('accounts/update', {
          custom_attributes: {
            ...this.currentAccount.custom_attributes,
            login_title: this.loginTitle,
          },
        });
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.LOGIN_TITLE_SAVED'));
      } catch (error) {
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.LOGIN_TITLE_SAVE_ERROR'));
      }
    },

    async saveWhiteLabelSenderInbox() {
      try {
        await this.$store.dispatch('accounts/update', {
          custom_attributes: {
            ...this.currentAccount.custom_attributes,
            white_label_sender_inbox_id: this.whiteLabelSenderInboxId || null,
          },
        });
        useAlert(
          this.$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.SAVED')
        );
      } catch (error) {
        useAlert(
          this.$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.ERROR')
        );
      }
    },

    senderInboxOption(inbox) {
      return this.$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.OPTION', {
        name: inbox.name,
        email: inbox.email,
      });
    },

    triggerFaviconUpload() {
      this.$refs.faviconInput.click();
    },

    onFaviconFileSelected(event) {
      const file = event.target.files[0];
      if (file && this.validateBrandingFile(file)) {
        this.cropperFaviconImageSrc = URL.createObjectURL(file);
        this.showFaviconCropper = true;
      }
      event.target.value = '';
    },

    closeFaviconCropper() {
      this.showFaviconCropper = false;
      this.releasePreviewUrl('cropperFaviconImageSrc');
    },

    async handleSaveFavicon(blob) {
      const formData = new FormData();
      formData.append('custom_favicon', blob, 'favicon.png');
      try {
        await this.$store.dispatch('accounts/update', formData);
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.FAVICON_UPDATED'));
        this.closeFaviconCropper();

        // Update favicon in DOM dynamically
        const faviconUrl = this.currentAccount.custom_favicon_url;
        if (faviconUrl) {
          const links = document.querySelectorAll("link[rel~='icon']");
          links.forEach(link => {
            link.href = faviconUrl;
            link.dataset.defaultHref = faviconUrl;
          });
        }
      } catch (error) {
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.FAVICON_UPDATE_ERROR'));
      }
    },

    triggerLogoLoginUpload() {
      this.$refs.logoLoginInput.click();
    },

    onLogoLoginFileSelected(event) {
      const file = event.target.files[0];
      if (file && this.validateBrandingFile(file)) {
        this.cropperLogoLoginImageSrc = URL.createObjectURL(file);
        this.showLogoLoginCropper = true;
      }
      event.target.value = ''; // reset
    },

    closeLogoLoginCropper() {
      this.showLogoLoginCropper = false;
      this.releasePreviewUrl('cropperLogoLoginImageSrc');
    },

    async handleSaveLogoLogin(blob) {
      const formData = new FormData();
      formData.append('custom_logo_login', blob, 'logo_login.png');
      try {
        await this.$store.dispatch('accounts/update', formData);
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.LOGIN_LOGO_UPDATED'));
        this.closeLogoLoginCropper();
      } catch (error) {
        useAlert(this.$t('ACCOUNT_SETTINGS.ALERTS.LOGIN_LOGO_UPDATE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
        class="!pt-0"
      >
        <form
          v-if="!uiFlags.isFetchingItem"
          class="grid gap-4"
          @submit.prevent="updateAccount"
        >
          <WithLabel
            name="account-name"
            :has-error="v$.name.$error"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
          >
            <NextInput
              id="account-name"
              v-model="name"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              :aria-invalid="v$.name.$error"
              :aria-describedby="v$.name.$error ? 'account-name-error' : null"
              @blur="v$.name.$touch"
            />
          </WithLabel>
          <WithLabel
            name="site-language"
            :has-error="v$.locale.$error"
            :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
          >
            <select
              id="site-language"
              v-model="locale"
              class="!mb-0 text-sm"
              :aria-invalid="v$.locale.$error"
              :aria-describedby="
                v$.locale.$error ? 'site-language-error' : null
              "
            >
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
          </WithLabel>
          <div
            v-if="featureInboundEmailEnabled"
            class="p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('GENERAL_SETTINGS.FORM.EMAIL_CONTINUITY.TITLE') }}
            </h3>
            <p
              v-if="inboundEmailContinuityConfigured"
              class="mt-1 text-body-main text-n-slate-11"
            >
              {{
                $t('GENERAL_SETTINGS.FORM.EMAIL_CONTINUITY.CONFIGURED', {
                  domain: inboundEmailDomain,
                })
              }}
            </p>
            <p v-else class="mt-1 text-body-main text-n-slate-11">
              {{ $t('GENERAL_SETTINGS.FORM.EMAIL_CONTINUITY.UNAVAILABLE') }}
            </p>
            <p class="mt-3 text-body-main text-n-slate-11">
              {{ $t('GENERAL_SETTINGS.FORM.EMAIL_CONTINUITY.HELP') }}
            </p>
          </div>

          <div>
            <NextButton blue :is-loading="isUpdating" type="submit">
              {{ $t('GENERAL_SETTINGS.SUBMIT') }}
            </NextButton>
          </div>
        </form>
      </SectionLayout>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />

      <SectionLayout
        v-if="!uiFlags.isFetchingItem"
        :title="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.DESCRIPTION')"
        class="mt-6"
      >
        <div v-if="hasModule('white_label')">
          <div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
            <WithLabel
              name="brand-name"
              :label="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.BRAND_NAME')"
              class="flex-1 !mb-0"
            >
              <NextInput
                id="brand-name"
                v-model="brandName"
                type="text"
                class="w-full !mb-0"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.BRAND_NAME_PLACEHOLDER')
                "
              />
            </WithLabel>
            <NextButton
              class="w-full sm:w-auto"
              :disabled="isUpdating"
              :is-loading="isUpdating"
              :aria-label="
                $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SAVE_BRAND_NAME')
              "
              @click="saveBrandName"
            >
              {{ $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SAVE_BRAND_NAME') }}
            </NextButton>
          </div>

          <div class="mb-4 mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
            <WithLabel
              name="login-title"
              :label="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.LOGIN_TITLE')"
              class="flex-1 !mb-0"
            >
              <NextInput
                id="login-title"
                v-model="loginTitle"
                type="text"
                class="w-full !mb-0"
                :placeholder="
                  $t(
                    'GENERAL_SETTINGS.FORM.WHITE_LABEL.LOGIN_TITLE_PLACEHOLDER'
                  )
                "
              />
            </WithLabel>
            <NextButton
              class="w-full sm:w-auto"
              :disabled="isUpdating"
              :is-loading="isUpdating"
              :aria-label="
                $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SAVE_LOGIN_TITLE')
              "
              @click="saveLoginTitle"
            >
              {{ $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SAVE_LOGIN_TITLE') }}
            </NextButton>
          </div>

          <div
            class="mb-6 rounded-xl border border-n-slate-5 bg-n-background p-4 dark:border-n-slate-8 dark:bg-n-solid-2"
          >
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.DESCRIPTION')
              }}
            </p>

            <div
              v-if="whiteLabelEmailInboxes.length"
              class="mt-4 flex flex-col gap-2 sm:flex-row sm:items-start"
            >
              <WithLabel
                name="white-label-sender-inbox"
                :label="
                  $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.LABEL')
                "
                class="min-w-0 flex-1 !mb-0"
              >
                <div class="relative">
                  <select
                    id="white-label-sender-inbox"
                    v-model="whiteLabelSenderInboxId"
                    class="!mb-0 h-12 w-full appearance-none rounded-lg border border-n-weak bg-n-background px-4 pr-11 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand dark:bg-n-solid-3"
                  >
                    <option value="">
                      {{
                        $t(
                          'GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.PLATFORM_DEFAULT'
                        )
                      }}
                    </option>
                    <option
                      v-for="inbox in whiteLabelEmailInboxes"
                      :key="inbox.id"
                      :value="String(inbox.id)"
                    >
                      {{ senderInboxOption(inbox) }}
                    </option>
                  </select>
                  <span
                    class="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-center"
                    aria-hidden="true"
                  >
                    <svg
                      class="size-4 text-n-slate-10"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="m4 6 4 4 4-4"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </WithLabel>
              <div class="sm:pt-7">
                <NextButton
                  class="h-12 w-full whitespace-nowrap sm:w-auto"
                  :disabled="isUpdating"
                  :is-loading="isUpdating"
                  :aria-label="
                    $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.SAVE')
                  "
                  @click="saveWhiteLabelSenderInbox"
                >
                  {{
                    $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.SAVE')
                  }}
                </NextButton>
              </div>
            </div>
            <p v-else class="mt-4 text-sm text-n-slate-11">
              {{ $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.SENDER_EMAIL.EMPTY') }}
            </p>
          </div>

          <div class="flex items-center gap-4 mt-2">
            <div
              v-if="currentAccount.custom_logo_url"
              class="w-16 h-16 rounded-md bg-white border border-slate-200 flex items-center justify-center p-2"
            >
              <img
                :src="currentAccount.custom_logo_url"
                class="max-w-full max-h-full object-contain"
                :alt="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.ACCOUNT_LOGO_ALT')"
              />
            </div>

            <button
              type="button"
              class="text-sm font-medium text-woot-500 hover:text-woot-600 underline"
              @click="triggerLogoUpload"
            >
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
              {{
                currentAccount.custom_logo_url
                  ? $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.CHANGE_LOGO')
                  : $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.UPLOAD_LOGO')
              }}
            </button>
            <input
              ref="logoInput"
              type="file"
              class="hidden"
              :aria-label="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.LOGO_INPUT')"
              accept="image/png,image/jpeg,image/webp"
              @change="onLogoFileSelected"
            />
          </div>

          <div class="flex items-center gap-4 mt-4">
            <div
              v-if="currentAccount.custom_favicon_url"
              class="w-16 h-16 rounded-md bg-white border border-slate-200 flex items-center justify-center p-2"
            >
              <img
                :src="currentAccount.custom_favicon_url"
                class="max-w-full max-h-full object-contain"
                :alt="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.FAVICON_ALT')"
              />
            </div>

            <button
              type="button"
              class="text-sm font-medium text-woot-500 hover:text-woot-600 underline"
              @click="triggerFaviconUpload"
            >
              {{
                currentAccount.custom_favicon_url
                  ? $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.CHANGE_FAVICON')
                  : $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.UPLOAD_FAVICON')
              }}
            </button>
            <input
              ref="faviconInput"
              type="file"
              class="hidden"
              :aria-label="
                $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.FAVICON_INPUT')
              "
              accept="image/png,image/jpeg,image/webp"
              @change="onFaviconFileSelected"
            />
          </div>

          <div class="flex items-center gap-4 mt-4">
            <div
              v-if="currentAccount.custom_logo_login_url"
              class="w-16 h-16 rounded-md bg-white border border-slate-200 flex items-center justify-center p-2"
            >
              <img
                :src="currentAccount.custom_logo_login_url"
                class="max-w-full max-h-full object-contain"
                :alt="$t('GENERAL_SETTINGS.FORM.WHITE_LABEL.LOGIN_LOGO_ALT')"
              />
            </div>

            <button
              type="button"
              class="text-sm font-medium text-woot-500 hover:text-woot-600 underline"
              @click="triggerLogoLoginUpload"
            >
              {{
                currentAccount.custom_logo_login_url
                  ? $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.CHANGE_LOGIN_LOGO')
                  : $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.UPLOAD_LOGIN_LOGO')
              }}
            </button>
            <input
              ref="logoLoginInput"
              type="file"
              class="hidden"
              :aria-label="
                $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.LOGIN_LOGO_INPUT')
              "
              accept="image/png,image/jpeg,image/webp"
              @change="onLogoLoginFileSelected"
            />
          </div>
        </div>
        <div
          v-else
          class="mt-2 p-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600 flex items-center gap-2"
        >
          <fluent-icon icon="lock-closed" size="16" class="text-slate-400" />
          <span>{{
            $t('GENERAL_SETTINGS.FORM.WHITE_LABEL.EXCLUSIVE_FEATURE')
          }}</span>
        </div>
      </SectionLayout>
    </div>

    <ImageCropperModal
      :show="showCropper"
      :image-src="cropperImageSrc"
      @close="closeCropper"
      @save="handleSaveLogo"
    />

    <ImageCropperModal
      :show="showFaviconCropper"
      :image-src="cropperFaviconImageSrc"
      :aspect-ratio="1"
      @close="closeFaviconCropper"
      @save="handleSaveFavicon"
    />

    <ImageCropperModal
      :show="showLogoLoginCropper"
      :image-src="cropperLogoLoginImageSrc"
      @close="closeLogoLoginCropper"
      @save="handleSaveLogoLogin"
    />

    <AccountId />
    <div v-if="!uiFlags.isFetchingItem && isOnLumeratecCloud">
      <AccountDelete />
    </div>
    <BuildInfo />
  </div>
</template>
